<template>
  <div class="flex min-h-4xs flex-col">
    <ui-toolbar />
    <div
      class="flex h-full flex-col-reverse overflow-y-hidden md:flex-row"
      :class="{
        'sm:!flex-row': hazards.activeView === HazardView.Both && windowSize.height.value < 750,
      }"
    >
      <ui-list
        v-show="!(hazards.activeView === HazardView.Map)"
        :class="{
          'flex !w-full flex-1': hazards.activeView === HazardView.List,
          'md:w-[480px] md:flex-initial xl:w-[684px]': hazards.activeView == HazardView.Both && windowSize.height.value >= 750,
          'sm:w-[480px] sm:flex-initial': hazards.activeView === HazardView.Both && windowSize.height.value < 750,
        }"
        :hazard-types="hazardTypes"
      />
      <ui-map
        :class="{
          hidden: hazards.activeView === HazardView.List,
          'flex-1': hazards.activeView === HazardView.Map,
          'h-1/2 sm:h-full md:flex-1': hazards.activeView === HazardView.Both && windowSize.height.value >= 750,
          'h-1/2 sm:h-full sm:flex-1': hazards.activeView === HazardView.Both && windowSize.height.value < 750,
        }"
      />
    </div>
    <ui-event-summary-modal v-if="eventModal.open" :modal="eventModal" />
    <ui-fire-danger-modal :modal="fdrModal" />
  </div>
</template>

<script setup lang="ts">
import { updateActiveFireBanLGAs, updateFireDangerRatings, useModal, usePoller } from '@/common';
import {
  HazardType,
  HazardView,
  MapMode,
  Poller,
  handleRouteChanges,
  initialiseHazardSources,
  useHazardsStore,
  useMapStore,
} from '@/features/hazards';
import { RouteNames } from '@/router';
import { useWindowSize } from '@vueuse/core';

import { onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';

const hazards = useHazardsStore();
const map = useMapStore();
const poller = usePoller();
const windowSize = useWindowSize();

const eventModal = useModal();
const fdrModal = useModal();

const router = useRouter();

const hazardTypes = [
  HazardType.Warning,
  HazardType.Incident,
  HazardType.Centre,
  HazardType.Closure,
  HazardType.Event,
  HazardType.TotalFireBans,
  HazardType.PrescribedBurns,
  HazardType.FireDangerRating,
];

watch([() => map.loaded, () => hazards.isLoadingHazards], async () => {
  if (hazards.isLoadingHazards || !map.loaded) {
    return;
  }

  initialiseHazardSources();

  // Handle route changes after the necessary data is loaded and ready
  const { query, params } = router.currentRoute.value;
  handleRouteChanges(params, query);
  map.setVisibleHazards();
});

watch(
  [
    () => hazards.getHazardFireShapePolygonsGeoSource,
    () => hazards.getFireDangerRatingsPolygonsGeoSource,
    () => hazards.filteredSectionHazards,
  ],
  async () => {
    if (hazards.isLoadingHazards || !map.loaded) {
      return;
    }
    initialiseHazardSources();
  }
);

watch(
  [
    () => hazards.filteredSectionHazards.fireDangerRatings,
    () => hazards.selectedDateIndex,
    () => map.loaded,
    () => hazards.mapMode,
    () => hazards.filteredSectionHazards.totalFireBans,
  ],
  () => {
    if (!map.loaded) {
      return;
    }

    map.setVisibleHazards();

    updateFireDangerRatings();
    updateActiveFireBanLGAs();
  }
);

watch(router.currentRoute, async (newRoute) => {
  const { query, params, name } = newRoute;
  if (name === RouteNames.Hazards) {
    hazards.setMapMode(MapMode.Default);
    return;
  }

  handleRouteChanges(params, query);
});

onMounted(async () => {
  await hazards.fetchAllHazards(hazardTypes);

  poller.registerPoller(
    Poller.fetchAllHazards,
    async () => {
      await hazards.fetchAllHazards(hazardTypes);
    },
    60 * 1000 // 1 minute
  );

  poller.startPoller(Poller.fetchAllHazards);
});

onUnmounted(() => {
  poller.stopPoller(Poller.fetchAllHazards);
});
</script>
